import React, { FC, useCallback } from 'react';
import styled from 'styled-components';

import NumberSpinner from '../../shared/common/NumberSpinner';
import CopiesListInfoButton from '../../shared/product/CopiesListInfoButton';
import SelectionlistActionButtons from '../selectionlists/SelectionlistActionButtons';
import CatalogueListItem from '../catalogue/CatalogueListItem';
import RemoveFromProductlistButton from '../selectionlists/RemoveFromProductlistButton';
import { ProductListItemProps } from '../../shared/common/ProductList';
import { ACTION_COPY, ACTION_MOVE, PRODUCT_CLASS_CAMPAIGN } from '../../store/constants';
import { Orderlist } from '../../../models/OrderList';
import { UsageCondition } from '../../../models/Product';
import { SubscriptionPeriodType } from '../../../models/Article';
import { ListActionTarget } from '../../../models/ListActionTarget';

const StButtonLine = styled.div`
    display: flex !important;
    justify-content: space-between;
    margin-bottom: 8px !important;
`;

const CartListItem: FC<ProductListItemProps> = props => {
    const {
        product,
        usageCondition,
        onUpdateUsageCondition = async () => {},
        onRemoveProductFromCart,
        onCopyAmountChange,
        onAddPrice,
        onAddToGroup
    } = props;

    const isCampaignProduct = product.productClass === PRODUCT_CLASS_CAMPAIGN;

    const handleAddPrice = useCallback(() => {
        // not supported within the cart (the product already is in the cart)
        return Promise.resolve();
    }, []);

    const handleSelectPrice = useCallback(
        (newUsageCondition: UsageCondition, subscriptionPeriodType?: SubscriptionPeriodType) =>
            onUpdateUsageCondition(product, newUsageCondition, subscriptionPeriodType),
        [onUpdateUsageCondition, product]
    );

    const handleCopyAmountChange = useCallback(
        (command: string, amount: number) => onCopyAmountChange?.(product, amount, command),
        [onCopyAmountChange, product]
    );

    const handleCopyToList = useCallback(
        async (list: Orderlist) => {
            await onAddPrice({
                options: product,
                target: ListActionTarget.List,
                listId: list.id,
                action: ACTION_COPY
            });
        },
        [onAddPrice, product]
    );

    const handleMoveToList = useCallback(
        async (list: Orderlist) => {
            await onAddPrice({
                options: product,
                target: ListActionTarget.List,
                listId: list.id,
                action: ACTION_MOVE
            });
        },
        [onAddPrice, product]
    );
    const handleAddToGroup = useCallback(
        (groupId: number) => onAddToGroup?.(groupId, product.id),
        [onAddToGroup, product]
    );

    const actionButtons = (
        <StButtonLine className="bt-line">
            <RemoveFromProductlistButton product={product} onRemoveProductFromList={onRemoveProductFromCart} />
            <SelectionlistActionButtons
                isCampaignProduct={isCampaignProduct}
                onCopyProductToList={handleCopyToList}
                onMoveProductToList={handleMoveToList}
                onAddToGroup={handleAddToGroup}
                showAddToGroup={!!onAddToGroup}
            />
            {product.salesStatus && product.salesStatus.key !== 'COMING_SOON' && (
                <CopiesListInfoButton product={product} usageCondition={usageCondition} />
            )}
            <NumberSpinner
                product={product}
                value={product.price?.copyAmount}
                min={0}
                onChange={handleCopyAmountChange}
            />
        </StButtonLine>
    );

    return (
        <CatalogueListItem
            {...props}
            isBuyingOptionEnabled={false}
            showSelectionLists={false}
            actionButtons={actionButtons}
            onAddPrice={handleAddPrice}
            onSelectPrice={handleSelectPrice}
            showSelectedPriceOnly={true}
            allowSelection={false}
        />
    );
};

export default CartListItem;
